/**
 * One account, on one screen: who they are, where they are right now, what they have played,
 * every ban they have ever had, the photos they uploaded, and the four actions an operator can
 * take on them — the last of which, deleting the account, is the only one nobody can undo.
 *
 * Reached by clicking a name on the Users tab. It is a **view of that tab** rather than a route
 * of its own, and that is deliberate: the console's tabs are component state (`AdminConsole`),
 * not routes, so a nested route here would mean restructuring `/admin` into a layout — a change
 * to every tab, to buy a URL for one of them. Back returns to the list with its filters and its
 * page intact, which is the thing a route would otherwise have been needed for.
 *
 * **Admin-only, at the middleware.** `admin.users.detail` is `adminProcedure`, like the roster it
 * is opened from: it joins the profile, the stats, the whole ban history and the sessions into
 * one answer, and the roles card already refused a moderator the roster on the grounds that
 * browsing accounts is a step toward finding the admins. A moderator's per-account view is the
 * Moderation tab's ten-row `findUsers` lookup, which is deliberately not this. The one thing
 * here a moderator *may* do — issue and lift a ban — is `moderatorProcedure` as it always was,
 * and reached from their own tab.
 *
 * The dialogs are the ones that already exist. `BanDialog` is shared with the Moderation tab and
 * the Users list, because the decision is the same one in all three and a second copy of the
 * form would be a second place to forget that the reason is shown to the player.
 */

import { useState } from 'react';
import type {
  AdminUserBanHistoryRow,
  AdminUserDetail,
  AdminUserMatchRow,
} from '../../../../server/src/trpc/routers/admin-users.js';
import { type BanTarget, BanDialog } from '@/components/admin/ban-dialog';
import type { DeleteAccountTarget } from '@/components/admin/delete-account-dialog';
import { Instant, formatInstant } from '@/components/admin/instant';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';

/** Mirrors `USER_ROLES` in the server's `db/schema/users.ts`; the column is what enforces it. */
const ROLE_OPTIONS = [
  { value: 'player', label: 'Player' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'admin', label: 'Admin' },
] as const;

type Role = (typeof ROLE_OPTIONS)[number]['value'];

export function UserDetailPanel({
  userId,
  canDelete,
  onBack,
  onDelete,
}: {
  userId: string;
  /**
   * Whether to draw the Delete account control. Admin only, and handed in rather than worked
   * out here: this component never reads a role, and the server is the access control either
   * way (`admin.users.remove` is `adminProcedure`).
   */
  canDelete: boolean;
  onBack: () => void;
  /**
   * Open the delete confirmation on this account. The dialog and the mutation live in
   * `UsersPanel`, because a successful deletion has to close *this* view — there is no account
   * left to look at — and the outcome has to be said on the list it returns to.
   */
  onDelete: (target: DeleteAccountTarget) => void;
}) {
  const utils = trpc.useUtils();
  const detail = trpc.admin.users.detail.useQuery({ userId });

  const [banning, setBanning] = useState<BanTarget | null>(null);
  const [lifting, setLifting] = useState<AdminUserBanHistoryRow | null>(null);

  const refresh = async () => {
    await Promise.all([
      utils.admin.users.detail.invalidate(),
      utils.admin.users.list.invalidate(),
      utils.admin.moderation.listBans.invalidate(),
    ]);
  };

  const setRole = trpc.admin.users.setRole.useMutation({ onSuccess: refresh });
  const banUser = trpc.admin.moderation.banUser.useMutation({
    onSuccess: async () => {
      setBanning(null);
      await refresh();
    },
  });
  const liftBan = trpc.admin.moderation.liftBan.useMutation({
    onSuccess: async () => {
      setLifting(null);
      await refresh();
    },
  });

  const data = detail.data ?? null;

  return (
    <section className="space-y-3" data-testid="user-detail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="outline" size="sm" onClick={onBack}>
          ← Back to users
        </Button>
        {data !== null && (
          <div className="flex items-center gap-2">
            {data.account.activeBan === null ? (
              <Button variant="destructive" size="sm" onClick={() => setBanning(data.account)}>
                Ban account
              </Button>
            ) : (
              <span className="text-sm font-medium text-destructive">
                Banned: {data.account.activeBan.reason}
              </span>
            )}
            {/* Beside the ban, and only for an admin. A ban is a row somebody can lift; this is
                the one control on the console nobody can undo, so it is an outline rather than a
                second filled destructive button sitting where a reflex lands. */}
            {canDelete && (
              <Button
                variant="outline"
                size="sm"
                aria-label={`Delete ${data.account.displayName}`}
                onClick={() => onDelete(data.account)}
              >
                Delete account
              </Button>
            )}
          </div>
        )}
      </div>

      {detail.error && (
        <p className="text-sm text-destructive" role="alert">
          {detail.error.message}
        </p>
      )}
      {setRole.error && (
        <p className="text-sm text-destructive" role="alert">
          {setRole.error.message}
        </p>
      )}
      {liftBan.error && (
        <p className="text-sm text-destructive" role="alert">
          {liftBan.error.message}
        </p>
      )}

      {detail.isLoading || data === null ? (
        <p className="py-4 text-center text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <ProfileCard
            data={data}
            rolePending={setRole.isPending}
            onSetRole={(role) => setRole.mutate({ userId, role })}
          />
          <StatsCard stats={data.stats} />
          <MatchesCard matches={data.recentMatches} />
          <BansCard bans={data.bans} onLift={setLifting} />
          <ImagesCard images={data.images} />
        </>
      )}

      {banning && (
        <BanDialog
          target={banning}
          serverError={banUser.error?.message ?? null}
          pending={banUser.isPending}
          onSubmit={({ duration, reason }) =>
            banUser.mutate({ userId: banning.id, duration, reason })
          }
          onClose={() => setBanning(null)}
        />
      )}

      {lifting && (
        <Dialog
          open
          title="Lift this ban?"
          description="They will be able to log in and join a room again straight away. The ban stays in the history with your name on it."
          onClose={() => setLifting(null)}
        >
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setLifting(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={liftBan.isPending}
              onClick={() => liftBan.mutate({ banId: lifting.id })}
            >
              {liftBan.isPending ? 'Lifting…' : 'Lift ban'}
            </Button>
          </div>
        </Dialog>
      )}
    </section>
  );
}

function ProfileCard({
  data,
  rolePending,
  onSetRole,
}: {
  data: AdminUserDetail;
  rolePending: boolean;
  onSetRole: (role: Role) => void;
}) {
  const { account } = data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {account.avatarUrl !== null && (
            <img
              src={account.avatarUrl}
              alt=""
              className="h-8 w-8 rounded-full object-cover"
              width={32}
              height={32}
            />
          )}
          <span>{account.displayName}</span>
          {account.isGuest && (
            <span className="rounded-full border border-border px-2 py-0.5 text-xs font-normal text-muted-foreground">
              Guest
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Field label="Email">{account.email ?? 'None (guest)'}</Field>
          <Field label="Account id">
            <span className="break-all font-mono text-xs">{account.id}</span>
          </Field>
          <Field label="Registered">
            <Instant iso={account.createdAt} />
          </Field>
          <Field label="Last seen">
            <Instant iso={account.lastSeenAt} />
          </Field>
          <Field label="Playing now">
            {account.presence === null ? (
              'Not connected'
            ) : (
              <span>
                {account.presence.spectator ? 'Watching' : 'Playing'} in{' '}
                <span className="font-mono text-xs">{account.presence.roomId}</span> on{' '}
                <span className="font-mono text-xs">{account.presence.hostId}</span>
              </span>
            )}
          </Field>
          <Field label="Country">
            {/* Resolved per connection from the request that minted the ticket and stored in no
                table, so it exists only while they are online — see `services/geo`. */}
            {account.presence?.country ?? 'Only known while they are connected'}
          </Field>
          <Field label="Sessions">
            {data.sessions.active} live
            {data.sessions.lastSignedInAt !== null && (
              <>
                {' · newest '}
                <Instant iso={data.sessions.lastSignedInAt} />
              </>
            )}
          </Field>
          <Field label="Last device">{data.sessions.lastUserAgent ?? 'Unknown'}</Field>
          {/*
           * **A SIGN-IN ADDRESS, AND THE LABEL SAYS SO** — see `lastAddressesByUser`. Nothing in
           * this product records an address per request, so what this is, is where the newest
           * session on the account was opened from, live or expired; an operator reading it as a
           * live location would be reading a week-old fact as a current one.
           *
           * Beside the country deliberately, which is the opposite case one field up: a country
           * is resolved per connection and stored in no table, so it exists only while somebody
           * is online, and this exists whether or not they are.
           */}
          <Field label="Signed in from">
            {account.lastIpAddress === null ? (
              'No session has recorded an address'
            ) : (
              <span data-testid="detail-last-ip" className="font-mono text-xs">
                {account.lastIpAddress}
              </span>
            )}
          </Field>
        </dl>

        {account.isGuest ? (
          <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            This is a guest. A guest row is reclaimed by a device id anybody can send, so banning
            one is a speed bump, not an identity: it ends the match they are in and stops this
            account being turned into a registered one, and they can start again as a new guest in
            seconds. Guests cannot hold a role until they register.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm font-medium" htmlFor="user-detail-role">
              Role
            </label>
            <select
              id="user-detail-role"
              className="h-9 w-36 rounded-lg border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={`Role for ${account.displayName}`}
              value={account.role}
              disabled={rolePending}
              onChange={(event) => onSetRole(event.target.value as Role)}
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              {ROLE_OPTIONS.every((option) => option.value !== account.role) && (
                <option value={account.role}>{account.role}</option>
              )}
            </select>
            {account.allowlisted && (
              <span className="text-xs text-muted-foreground">
                In ADMIN_EMAILS; will be granted admin again on next sign-in.
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Lifetime totals and personal bests, apart from each other.
 *
 * A best is the high-water mark of one run and a total is everything added up; the profile page
 * keeps them in two cards for exactly that reason, and mixing them in one grid reads wrongly.
 */
function StatsCard({ stats }: { stats: AdminUserDetail['stats'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Stats</CardTitle>
      </CardHeader>
      <CardContent>
        {stats === null ? (
          <p className="text-sm text-muted-foreground">
            No stats row yet — this account has not finished a run.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Personal bests
              </h3>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <Field label="Best mass">{stats.bestMass.toLocaleString()}</Field>
                <Field label="Best pellets">{stats.bestPellets.toLocaleString()}</Field>
                <Field label="Longest run">{formatSeconds(stats.bestSurvivalSeconds)}</Field>
              </dl>
            </div>
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Lifetime totals
              </h3>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <Field label="Games">{stats.gamesPlayed.toLocaleString()}</Field>
                <Field label="Kills">{stats.totalKills.toLocaleString()}</Field>
                <Field label="Deaths">{stats.totalDeaths.toLocaleString()}</Field>
                <Field label="Pellets">{stats.totalPelletsEaten.toLocaleString()}</Field>
                <Field label="Mass gained">{stats.totalMassGained.toLocaleString()}</Field>
                <Field label="Splits">{stats.totalSplits.toLocaleString()}</Field>
                <Field label="Deathmatch wins">{stats.roundWins.toLocaleString()}</Field>
                <Field label="Playtime">{formatSeconds(stats.playtimeSeconds)}</Field>
              </dl>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MatchesCard({ matches }: { matches: readonly AdminUserMatchRow[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recent matches</CardTitle>
      </CardHeader>
      <CardContent>
        {matches.length === 0 ? (
          <p className="text-sm text-muted-foreground">No finished runs.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Ended</th>
                  <th className="py-2 pr-3 font-medium">Room</th>
                  <th className="py-2 pr-3 font-medium">Mass</th>
                  <th className="py-2 pr-3 font-medium">Kills</th>
                  <th className="py-2 pr-3 font-medium">Lasted</th>
                  <th className="py-2 font-medium">How it ended</th>
                </tr>
              </thead>
              <tbody data-testid="user-match-rows">
                {matches.map((match) => (
                  <tr key={match.id} className="border-b border-border/60 last:border-0">
                    <td className="py-2 pr-3">
                      <Instant className="text-xs text-muted-foreground" iso={match.endedAt} />
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{match.roomId}</td>
                    <td className="py-2 pr-3">
                      {match.finalMass.toLocaleString()}
                      <span className="text-xs text-muted-foreground">
                        {' '}
                        (peak {match.peakMass.toLocaleString()})
                      </span>
                    </td>
                    <td className="py-2 pr-3">{match.kills}</td>
                    <td className="py-2 pr-3">{formatSeconds(match.durationSeconds)}</td>
                    <td className="py-2">
                      {match.endReason}
                      {match.killedByName !== null && (
                        <span className="text-xs text-muted-foreground">
                          {' '}
                          by {match.killedByName}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Every ban this account has ever had, live or not.
 *
 * `user_bans` is append-only, so this is the table itself: lifting one writes `lifted_at` onto
 * the row rather than deleting it, and an expired ban is simply one whose instant has passed.
 * "Has this account been banned four times before" is the question the history exists to answer,
 * and a boolean column never could.
 */
function BansCard({
  bans,
  onLift,
}: {
  bans: readonly AdminUserBanHistoryRow[];
  onLift: (ban: AdminUserBanHistoryRow) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Ban history</CardTitle>
      </CardHeader>
      <CardContent>
        {bans.length === 0 ? (
          <p className="text-sm text-muted-foreground">This account has never been banned.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Issued</th>
                  <th className="py-2 pr-3 font-medium">Reason</th>
                  <th className="py-2 pr-3 font-medium">Length</th>
                  <th className="py-2 pr-3 font-medium">By</th>
                  <th className="py-2 font-medium">State</th>
                </tr>
              </thead>
              <tbody data-testid="user-ban-rows">
                {bans.map((ban) => (
                  <tr key={ban.id} className="border-b border-border/60 last:border-0">
                    <td className="py-2 pr-3">
                      <Instant className="text-xs text-muted-foreground" iso={ban.createdAt} />
                    </td>
                    <td className="max-w-64 py-2 pr-3">{ban.reason}</td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {ban.durationLabel}
                      <div className="text-xs">
                        {ban.expiresAt === null ? (
                          'Permanent'
                        ) : (
                          <>
                            {'until '}
                            <Instant iso={ban.expiresAt} />
                          </>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {ban.issuedByName ?? 'Deleted account'}
                    </td>
                    <td className="py-2">
                      {ban.active ? (
                        <div className="space-y-1">
                          <span className="block text-xs font-medium text-destructive">
                            In force
                          </span>
                          <Button variant="outline" size="sm" onClick={() => onLift(ban)}>
                            Lift
                          </Button>
                        </div>
                      ) : ban.liftedAt !== null ? (
                        <span className="text-xs text-muted-foreground">
                          Lifted by {ban.liftedByName ?? 'a deleted account'}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Expired</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ImagesCard({ images }: { images: AdminUserDetail['images'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Images</CardTitle>
      </CardHeader>
      <CardContent>
        {images.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing uploaded.</p>
        ) : (
          <ul className="flex flex-wrap gap-3" data-testid="user-image-rows">
            {images.map((image) => (
              <li key={image.id} className="w-24 text-center">
                <img
                  src={image.url}
                  alt={`${image.kind} uploaded ${formatInstant(image.createdAt)}`}
                  className="h-24 w-24 rounded-lg border border-border object-cover"
                  width={96}
                  height={96}
                />
                <span className="mt-1 block text-xs text-muted-foreground">
                  {image.kind}
                  {image.selected && ' · in play'}
                </span>
                {/* An unapproved image is drawn here — this is the console — but nobody outside
                    this account sees it in the game. The verdict is given on the Moderation
                    tab, where the queue is. */}
                {image.status !== 'approved' && (
                  <span className="mt-0.5 block text-xs font-medium text-muted-foreground">
                    {image.status === 'pending' ? 'Pending review' : 'Rejected'}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="break-words">{children}</dd>
    </div>
  );
}

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}
